import type { V2 } from '../../core/math';
import { Rng } from '../../core/rng';
import { DungeonBuilder, type DungeonKit, type DungeonPaint } from '../../zones/DungeonBuilder';
import type { ZoneLayout } from '../../zones/types';

/** Walkable radius of every colossus bowl (≈ 88 m across). */
export const COLOSSUS_ARENA_RADIUS = 44;

interface ArenaTheme {
  id: string;
  name: string;
  subtitle: string;
  biome: string;
  seed: number;
  ground: ZoneLayout['ground'];
  groundScale?: [number, number, number, number];
  paint: DungeonPaint;
  accentAmount: number;
  ambient: ZoneLayout['ambient'];
  mapTint: number;
  wallHeight: number;
  kit: DungeonKit;
  dress: (d: DungeonBuilder) => void;
}

const R = COLOSSUS_ARENA_RADIUS;

/** Cover piece at a clock position around the centre; `collider` is the model radius at scale 1. */
function cover(d: DungeonBuilder, hour: number, r: number, model: string, scale: number, collider: number, tint?: number): V2 {
  const p = DungeonBuilder.clock(0, 0, r, hour);
  const extra: { collider: { r: number }; tint?: number } = { collider: { r: collider } };
  if (tint !== undefined) extra.tint = tint;
  d.prop(model, p.x, p.z, { scale, rot: hour * 1.7, ...extra });
  d.reserve(p, collider * scale + 1.5);
  return p;
}

function buildArena(t: ArenaTheme): ZoneLayout {
  const d = new DungeonBuilder(
    {
      id: t.id, name: t.name, subtitle: t.subtitle, kind: 'colossus', biome: t.biome, levelRange: [30, 30], music: 'boss',
      width: 140, depth: 140, seed: t.seed, ground: t.ground, groundScale: t.groundScale, ambient: t.ambient, mapTint: t.mapTint,
      wallHeight: t.wallHeight, nearWallHeight: 1.5, wallSlope: 0.6, wallRough: 1.4, floorNoise: 0.25, paint: t.paint, accentAmount: t.accentAmount, kit: t.kit,
    },
    new Rng(t.id),
  );
  d.room(0, 0, R, { walls: false });
  d.spawn('boss', 0, 0, 0);
  d.reserve({ x: 0, z: 0 }, 12);
  d.spawn('start', 0, 38, Math.PI);
  t.dress(d);
  return d.build();
}

/** Emberdeep Caldera — magma cave for the Cinderhide Behemoth (ilvl 250). */
export function buildColossusCinderhide(): ZoneLayout {
  const BASALT = 0xb09a90;
  return buildArena({
    id: 'colossus_cinderhide', name: 'Emberdeep Caldera', subtitle: 'Colossus Hunt — Cinderhide Behemoth', biome: 'abyss', seed: 2601,
    ground: ['rock', 'ash', 'dirt', 'cobble'], groundScale: [7, 6, 5, 4], paint: { floor: 0, path: 2, wall: 0, accent: 1 }, accentAmount: 0.3,
    ambient: 'embers', mapTint: 0x2a1410, wallHeight: 12,
    kit: {
      rocks: [
        { model: 'cliff_chunk', r: 2.9, scale: [0.6, 1.1], tint: BASALT, weight: 3 },
        { model: 'rock_c', r: 0.7, scale: [2.0, 3.4], tint: BASALT, weight: 2 },
      ],
      rockSpacing: 3.2,
      clutter: [{ model: 'rock_b', scale: [0.3, 0.6], tint: BASALT, weight: 2 }, { model: 'bones' }],
      clutterChance: 0.3,
    },
    dress: (d) => {
      // Glowing fissures radiating from the caldera floor.
      const cracks: V2[][] = [
        [{ x: -6, z: -10 }, { x: -14, z: -20 }, { x: -20, z: -34 }],
        [{ x: 9, z: -6 }, { x: 22, z: -9 }, { x: 36, z: -18 }],
        [{ x: 8, z: 9 }, { x: 18, z: 20 }, { x: 24, z: 34 }],
        [{ x: -10, z: 5 }, { x: -24, z: 8 }, { x: -38, z: 16 }],
        [{ x: 2, z: -12 }, { x: 4, z: -26 }, { x: -1, z: -40 }],
      ];
      for (const c of cracks) d.paintLine(c, 1.1, 1, 1.2);
      cover(d, 1.5, 24, 'rock_c', 2.8, 0.7, BASALT);
      cover(d, 4.2, 27, 'rock_c', 2.5, 0.7, BASALT);
      cover(d, 7.6, 23, 'rock_c', 3.0, 0.7, BASALT);
      cover(d, 10.3, 27, 'rock_c', 2.6, 0.7, BASALT);
      cover(d, 12, 33, 'rock_a', 2.2, 1.05, BASALT);
      cover(d, 3, 36, 'rock_a', 1.9, 1.05, BASALT);
      cover(d, 9, 35, 'rock_a', 2.0, 1.05, BASALT);
      d.scatterBeyond([{ model: 'cliff_chunk', weight: 2, scale: [0.9, 1.6], tint: BASALT }, { model: 'rock_c', weight: 2, scale: [2.5, 4.5], tint: BASALT }], 60, 6, [5, 24], [{ model: 'rock_b', scale: [0.8, 1.4], tint: BASALT }]);
    },
  });
}

/** Moonveil Glade — moonlit forest clearing for Gloomfang (ilvl 290). */
export function buildColossusGloomfang(): ZoneLayout {
  const STONE = 0xc8ccd8;
  return buildArena({
    id: 'colossus_gloomfang', name: 'Moonveil Glade', subtitle: 'Colossus Hunt — Gloomfang', biome: 'meadow', seed: 2602,
    ground: ['grass', 'dirt', 'moss', 'rock'], groundScale: [4, 5, 5, 7], paint: { floor: 0, path: 1, wall: 3, accent: 2 }, accentAmount: 0.7,
    ambient: 'motes', mapTint: 0x1a2430, wallHeight: 5,
    kit: {
      rocks: [
        { model: 'tree_pine', r: 0.4, scale: [1.1, 1.6], weight: 3 },
        { model: 'tree_oak', r: 0.4, scale: [1.0, 1.5], weight: 3 },
        { model: 'tree_dead', r: 0.35, scale: [1.0, 1.4], weight: 1 },
        { model: 'rock_a', r: 1.1, scale: [0.9, 1.6], tint: STONE, weight: 1 },
      ],
      nearRocks: [{ model: 'bush', scale: [0.9, 1.3], weight: 3 }, { model: 'bush_flower', scale: [0.8, 1.2], weight: 2 }, { model: 'rock_b', scale: [0.8, 1.3], tint: STONE }],
      rockSpacing: 2.4,
      clutter: [{ model: 'mushroom_cluster', weight: 2, scale: [0.6, 1.0] }, { model: 'bush', weight: 2, scale: [0.7, 1.1] }, { model: 'grass_tuft', weight: 3 }],
      clutterChance: 0.45,
    },
    dress: (d) => {
      // A broken ring of standing stones, fallen trunks for cover.
      for (const [i, h] of [0.8, 2.6, 4.4, 5.3, 7.6, 9.2, 11].entries()) cover(d, h, i % 2 === 0 ? 22 : 24, 'pillar_ruin', i === 3 ? 1.1 : 1.45, 0.6, STONE);
      for (const [h, r, rot] of [[3.4, 32, 0.4], [8.4, 31, 2.1], [11.8, 36, 1.2]] as const) {
        const p = DungeonBuilder.clock(0, 0, r, h);
        d.prop('log', p.x, p.z, { scale: 1.6, rot, collider: { w: 2.7, d: 0.7 } });
        d.reserve(p, 3);
      }
      d.paintRing(0, 0, 10.5, 12, 1, 0.8);
      d.scatterFloor([{ model: 'grass_tuft', weight: 4, scale: [0.8, 1.3], noShadow: true }, { model: 'mushroom_cluster', weight: 1, scale: [0.5, 0.8], noShadow: true }, { model: 'bush_flower', weight: 1, scale: [0.6, 0.9], noShadow: true }], 70, 3.5, { margin: 1 });
      d.scatterBeyond([{ model: 'tree_pine', weight: 3, scale: [1.2, 1.8] }, { model: 'tree_oak', weight: 3, scale: [1.1, 1.7] }, { model: 'tree_dead', weight: 1, scale: [1.0, 1.5] }], 220, 3.4, [4, 26], [{ model: 'bush', scale: [1.0, 1.5] }, { model: 'rock_b', scale: [0.8, 1.4], tint: STONE }]);
    },
  });
}

/** Rimeglass Mere — frozen lake for the Rimebound Wyrm (ilvl 330). */
export function buildColossusRimebound(): ZoneLayout {
  const FROST = 0xe8f0ff;
  return buildArena({
    id: 'colossus_rimebound', name: 'Rimeglass Mere', subtitle: 'Colossus Hunt — Rimebound Wyrm', biome: 'frost', seed: 2603,
    ground: ['ice', 'snow', 'rock', 'cobble'], groundScale: [8, 6, 7, 4], paint: { floor: 0, path: 1, wall: 2, accent: 1 }, accentAmount: 0.35,
    ambient: 'snow', mapTint: 0x2a3a50, wallHeight: 9,
    kit: {
      rocks: [
        { model: 'cliff_chunk', r: 2.9, scale: [0.6, 1.0], tint: FROST, weight: 3 },
        { model: 'tree_pine_snow', r: 0.4, scale: [1.1, 1.6], weight: 3 },
        { model: 'ice_crystal', r: 0.8, scale: [1.0, 1.8], weight: 1.5 },
      ],
      nearRocks: [{ model: 'rock_b', scale: [0.8, 1.3], tint: FROST, weight: 2 }, { model: 'ice_crystal', scale: [0.35, 0.55] }],
      rockSpacing: 2.8,
      clutter: [{ model: 'rock_b', scale: [0.3, 0.6], tint: FROST, weight: 2 }, { model: 'ice_crystal', scale: [0.25, 0.4] }],
      clutterChance: 0.3,
    },
    dress: (d) => {
      // Snowy shore around the frozen lake.
      d.paintRing(0, 0, 31, 50, 1, 3);
      cover(d, 1.2, 25, 'ice_crystal', 1.9, 0.8);
      cover(d, 3.8, 29, 'ice_crystal', 1.6, 0.8);
      cover(d, 6.9, 31, 'ice_crystal', 1.7, 0.8);
      cover(d, 8.6, 24, 'ice_crystal', 2.0, 0.8);
      cover(d, 10.9, 30, 'ice_crystal', 1.7, 0.8);
      cover(d, 12, 36, 'rock_a', 1.8, 1.05, FROST);
      d.scatterBeyond([{ model: 'tree_pine_snow', weight: 4, scale: [1.2, 1.8] }, { model: 'cliff_chunk', weight: 1, scale: [0.8, 1.3], tint: FROST }, { model: 'ice_crystal', weight: 1, scale: [1.2, 2.2] }], 140, 4, [5, 24], [{ model: 'rock_b', scale: [0.8, 1.3], tint: FROST }]);
    },
  });
}

/** Stormglass Basin — desert basin for the Dunestrider (ilvl 370). */
export function buildColossusDunestrider(): ZoneLayout {
  const SANDSTONE = 0xf0d0a0;
  return buildArena({
    id: 'colossus_dunestrider', name: 'Stormglass Basin', subtitle: 'Colossus Hunt — Dunestrider', biome: 'desert', seed: 2604,
    ground: ['sand', 'dirt', 'rock', 'dryGrass'], groundScale: [6, 5, 7, 5], paint: { floor: 0, path: 1, wall: 2, accent: 1 }, accentAmount: 0.45,
    ambient: 'none', mapTint: 0x4a3a24, wallHeight: 12,
    kit: {
      rocks: [
        { model: 'cliff_chunk', r: 2.9, scale: [0.7, 1.2], tint: SANDSTONE, weight: 3 },
        { model: 'rock_c', r: 0.7, scale: [1.8, 3.2], tint: SANDSTONE, weight: 1.5 },
        { model: 'cactus', r: 0.4, scale: [0.9, 1.3], weight: 1 },
      ],
      rockSpacing: 3,
      clutter: [{ model: 'bones', weight: 2, scale: [1, 1.8] }, { model: 'rock_b', scale: [0.3, 0.6], tint: SANDSTONE, weight: 2 }, { model: 'cactus', scale: [0.5, 0.7] }],
      clutterChance: 0.3,
    },
    dress: (d) => {
      // Ruined colonnade (north-east) and weathered boulders.
      for (const [h, r, s] of [[1.4, 25, 1.3], [2.0, 27, 1.0], [2.6, 24, 1.4]] as const) cover(d, h, r, 'pillar_ruin', s, 0.6, SANDSTONE);
      cover(d, 5, 28, 'rock_a', 2.2, 1.05, SANDSTONE);
      cover(d, 8, 24, 'rock_a', 2.0, 1.05, SANDSTONE);
      cover(d, 10.5, 30, 'rock_a', 2.4, 1.05, SANDSTONE);
      for (const [h, r] of [[6.8, 34], [11.6, 18]] as const) {
        const p = DungeonBuilder.clock(0, 0, r, h);
        d.prop('bones', p.x, p.z, { scale: 3.2, rot: h, noShadow: true });
      }
      d.scatterBeyond([{ model: 'cliff_chunk', weight: 2, scale: [1.0, 1.7], tint: SANDSTONE }, { model: 'rock_c', weight: 2, scale: [2.5, 4.5], tint: SANDSTONE }, { model: 'cactus', weight: 1, scale: [1, 1.4] }], 70, 6, [5, 24], [{ model: 'rock_b', scale: [0.8, 1.4], tint: SANDSTONE }]);
    },
  });
}
